// The weekly tournament played live as a pair (Paul, 18 Oct 2026). The pair sits North-South and
// the BEN bot plays East-West; both partners have to be here, each bids and plays their own hand,
// and a declarer plays dummy. The server (partnerHub function) holds the game; this page shows it
// and long-polls ("watch") so each move appears on both screens straight away.
// Your own seat is always drawn at the bottom. Wording is neutral structural labels.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import BiddingBox from "../PlayTable/BiddingBox";
import { ph, phAway, testUid } from "./phApi";
import { Avatar, CallText, ContractText, SEAT_NAME, SYM, rankText, suitCls, signed } from "./phUi";
import "./PartnerHub.css";

export const RING = ["N", "E", "S", "W"];
const AUCTION_COLS = ["W", "N", "E", "S"];
export const LIST_ORDER = ["S", "H", "D", "C"];
const LAST_TRICK_MS = 1500;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const same = (a, b) => a && b && a.suit === b.suit && a.rank === b.rank;
export function positions(me) {
  const i = RING.indexOf(me);
  return { bottom: me, left: RING[(i + 1) % 4], top: RING[(i + 2) % 4], right: RING[(i + 3) % 4] };
}
export const posOf = (pos, seat) => Object.keys(pos).find((k) => pos[k] === seat);
export const vulSeats = (vul) => (vul === "Both" ? RING : vul === "NS" ? ["N", "S"] : vul === "EW" ? ["E", "W"] : []);

export function Card({ card, onClick, playable, small, dim }) {
  const content = (
    <>
      <span className="pth-cRank">{rankText(card.rank)}</span>
      <span className="pth-cSuit">{SYM[card.suit]}</span>
    </>
  );
  const cls = `pth-c ${suitCls(card.suit)} ${small ? "pth-c--sm" : ""} ${playable ? "is-playable" : ""} ${dim ? "is-dim" : ""}`;
  if (!onClick) return <span className={cls}>{content}</span>;
  return (
    <button type="button" className={cls} onClick={onClick} disabled={!playable} aria-label={`${rankText(card.rank)} of ${{ S: "spades", H: "hearts", D: "diamonds", C: "clubs" }[card.suit]}`}>
      {content}
    </button>
  );
}

// A hand laid out as a fan of cards (your own, or a dummy above you).
export function Fan({ cards, legal, canPlay, onPlay, small }) {
  return (
    <div className={`pth-fan ${small ? "pth-fan--sm" : ""}`}>
      {cards.map((c) => {
        const ok = canPlay && legal.some((l) => same(l, c));
        return <Card key={c.suit + c.rank} card={c} small={small} playable={ok} dim={canPlay && !ok} onClick={canPlay ? () => onPlay(c) : undefined} />;
      })}
    </div>
  );
}

// A hand shown at the side: one row per suit.
export function SuitRows({ cards, legal, canPlay, onPlay }) {
  return (
    <div className="pth-rows">
      {LIST_ORDER.map((s) => {
        const inSuit = cards.filter((c) => c.suit === s);
        return (
          <div key={s} className="pth-row">
            <span className={`pth-rowSym ${suitCls(s)}`}>{SYM[s]}</span>
            {inSuit.length ? inSuit.map((c) => {
              const ok = canPlay && legal.some((l) => same(l, c));
              return canPlay
                ? <button key={c.rank} type="button" className={`pth-rowCard ${ok ? "is-playable" : ""}`} disabled={!ok} onClick={() => onPlay(c)}>{rankText(c.rank)}</button>
                : <span key={c.rank} className="pth-rowCard">{rankText(c.rank)}</span>;
            }) : <span className="pth-muted">—</span>}
          </div>
        );
      })}
    </div>
  );
}

export function Backs({ n }) {
  return (
    <div className="pth-backs" aria-label={`${n} cards`}>
      {Array.from({ length: Math.min(n, 13) }, (_, i) => <span key={i} className="pth-back" />)}
    </div>
  );
}

function Plate({ seat, t, board, me }) {
  const human = seat === "N" || seat === "S";
  const name = human ? t.names[seat] : SEAT_NAME[seat];
  const online = human ? t.here[seat] : undefined;
  const turn = board && board.toAct === seat && board.phase !== "done";
  const thinking = t.thinking === seat;
  const vul = board && vulSeats(board.vul).includes(seat);
  let sub = SEAT_NAME[seat];
  if (!human) sub = "Bot";
  else if (seat === me) sub = `${SEAT_NAME[seat]} · you`;
  else sub = `${SEAT_NAME[seat]} · partner`;
  if (board && board.phase === "play" && seat === board.dummy) sub += " · dummy";
  return (
    <div className={`pth-plate ${turn ? "is-turn" : ""} ${vul ? "is-vul" : ""}`}>
      {human ? <Avatar name={name} online={online} size="sm" /> : <span className="pth-bot" aria-hidden="true">{thinking ? <span className="pth-dots3"><i /><i /><i /></span> : "🤖"}</span>}
      <span className="pth-plateTxt">
        <b>{human ? name : SEAT_NAME[seat]}</b>
        <small>{sub}</small>
      </span>
      {board && board.dealer === seat && <span className="pth-dealer" title="Dealer">D</span>}
    </div>
  );
}

export function Auction({ board }) {
  const lead = AUCTION_COLS.indexOf(board.dealer);
  const cells = [...Array(lead).fill(null), ...board.auction];
  const next = board.phase === "auction" ? board.toAct : null;
  if (next) cells.push({ pending: true });
  const rows = Math.max(1, Math.ceil(cells.length / 4));
  return (
    <table className="pth-auction">
      <thead><tr>{AUCTION_COLS.map((s) => <th key={s} className={vulSeats(board.vul).includes(s) ? "is-vul" : ""}>{SEAT_NAME[s]}</th>)}</tr></thead>
      <tbody>
        {Array.from({ length: rows }, (_, r) => (
          <tr key={r}>
            {AUCTION_COLS.map((s, c) => {
              const x = cells[r * 4 + c];
              if (!x) return <td key={s} />;
              if (x.pending) return <td key={s}><span className="pth-call pth-call--q">…</span></td>;
              return (
                <td key={s} title={x.explanation || undefined}>
                  <CallText call={x.call} />{x.alert && <sup className="pth-alert">!</sup>}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function Trick({ plays, pos, winner }) {
  return (
    <div className="pth-trick" aria-label="Cards played to this trick">
      {plays.map((p) => (
        <div key={p.seat} className={`pth-trickCard pth-trickCard--${posOf(pos, p.seat)} ${winner === p.seat ? "is-winner" : ""}`}>
          <Card card={p.card} />
        </div>
      ))}
    </div>
  );
}

export function ClaimBox({ left, onClaim, onCancel, busy, msg }) {
  const [n, setN] = useState(left);
  return (
    <div className="pth-modalBack" role="dialog" aria-label="Claim">
      <div className="pth-modal">
        <h2 className="pth-h2">Claim</h2>
        <p className="pth-muted">How many of the remaining {left} tricks?</p>
        <div className="pth-claimNums">
          {Array.from({ length: left + 1 }, (_, i) => (
            <button key={i} type="button" className={`pth-num ${i === n ? "is-on" : ""}`} onClick={() => setN(i)}>{i}</button>
          ))}
        </div>
        {msg && <p className="pth-err">{msg}</p>}
        <div className="pth-actions">
          <button type="button" className="pth-btn pth-btn--primary" disabled={busy} onClick={() => onClaim(n)}>{busy ? "Checking…" : `Claim ${n}`}</button>
          <button type="button" className="pth-btn pth-btn--quiet" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export function DealCross({ board, me }) {
  const pos = positions(me);
  const H = ({ seat }) => (
    <div className={`pth-xHand pth-xHand--${posOf(pos, seat)}`}>
      <div className="pth-xSeat">{SEAT_NAME[seat]}</div>
      {LIST_ORDER.map((s) => (
        <div key={s} className="pth-xSuit">
          <span className={suitCls(s)}>{SYM[s]}</span> {(board.hands[seat] || []).filter((c) => c.suit === s).map((c) => rankText(c.rank)).join(" ") || "—"}
        </div>
      ))}
    </div>
  );
  return (
    <div className="pth-cross">
      {RING.map((s) => <H key={s} seat={s} />)}
      <div className="pth-xMid">Board {board.boardNo}</div>
    </div>
  );
}

function ResultCard({ board, me }) {
  const r = board.result;
  const need = board.contract ? board.contract.level + 6 : 0;
  const over = r && board.contract ? r.declarerTricks - need : 0;
  return (
    <section className="pth-card pth-result" aria-label={`Board ${board.boardNo} result`}>
      <div className="pth-resultTop">
        <div>
          <div className="pth-label">Board {board.boardNo}</div>
          <div className="pth-resultContract">
            <ContractText contract={board.contract} declarer={board.declarer} />
            {board.contract && <span className="pth-over">{over === 0 ? "=" : signed(over)}</span>}
          </div>
          {board.claimed !== null && board.claimed !== undefined && <div className="pth-muted pth-small">Claimed</div>}
        </div>
        <div className={`pth-score ${r.scoreNS > 0 ? "is-plus" : r.scoreNS < 0 ? "is-minus" : ""}`}>
          <div className="pth-label">North-South</div>
          {signed(r.scoreNS)}
        </div>
      </div>
      <div className="pth-resultBody">
        <DealCross board={board} me={me} />
        <div className="pth-resultAuction"><Auction board={board} /></div>
      </div>
    </section>
  );
}

function Standings({ pairId, onClose }) {
  const [s, setS] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { ph("standings", { pairId }).then(setS).catch((e) => setErr(e.message)); }, [pairId]);
  return (
    <div className="pth-modalBack" role="dialog" aria-label="Leaderboard" onClick={onClose}>
      <div className="pth-modal pth-modal--wide" onClick={(e) => e.stopPropagation()}>
        <div className="pth-modalHead">
          <h2 className="pth-h2">Leaderboard</h2>
          <button type="button" className="pth-x" onClick={onClose} aria-label="Close">×</button>
        </div>
        {err ? <p className="pth-err">{err}</p> : !s ? <p className="pth-muted">Loading…</p> : (
          <>
            <p className="pth-muted pth-small">Week of {s.weekId} · {s.field} {s.field === 1 ? "entry" : "entries"} · IMPs (Butler)</p>
            <ol className="pth-lb">
              {s.top.map((r) => (
                <li key={r.rank} className={r.us ? "is-us" : ""}>
                  <span className="pth-lbRank">{r.rank}</span>
                  <span className="pth-lbName">{r.names.join(" & ")}</span>
                  <span className="pth-lbImps">{signed(r.imps)}</span>
                </li>
              ))}
              {s.ours && s.ours.rank > 10 && (
                <li className="is-us"><span className="pth-lbRank">{s.ours.rank}</span><span className="pth-lbName">{s.ours.names.join(" & ")}</span><span className="pth-lbImps">{signed(s.ours.imps)}</span></li>
              )}
            </ol>
          </>
        )}
      </div>
    </div>
  );
}

export default function LiveTable({ pairId, uid, authReady }) {
  const signedIn = !!uid || !!testUid();
  const [t, setT] = useState(null);
  const [err, setErr] = useState("");
  const [net, setNet] = useState("");
  const [toast, setToast] = useState("");
  const [sending, setSending] = useState(false);
  const [showLB, setShowLB] = useState(false);
  const [claim, setClaim] = useState(null); // { busy, msg }
  const [lastShown, setLastShown] = useState(null); // the completed trick held on the table for a moment
  // When you invited your partner (from the hub, or the button in the waiting room).
  const [invitedAt, setInvitedAt] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("invited") ? Date.now() : 0; } catch (e) { return 0; }
  });
  const seenTrick = useRef(null);
  const trickTimer = useRef(null);

  // Load, then long-poll for changes for as long as the page is open.
  useEffect(() => {
    if (!signedIn) return undefined;
    let alive = true;
    const ctrl = new AbortController();
    (async () => {
      let v = null;
      try {
        const r = await ph("table", { pairId }, { signal: ctrl.signal });
        if (!alive) return;
        setT(r.table);
        v = r.table.v;
      } catch (e) {
        if (alive && e.name !== "AbortError") setErr(e.message);
        return;
      }
      while (alive) {
        try {
          const r = await ph("watch", { pairId, v }, { signal: ctrl.signal });
          if (!alive) break;
          setT(r.table);
          setNet("");
          v = r.table.v;
        } catch (e) {
          if (!alive || e.name === "AbortError") break;
          setNet(e.network ? "Reconnecting…" : e.message);
          await sleep(2500);
        }
      }
    })();
    const leave = () => phAway(pairId);
    window.addEventListener("pagehide", leave);
    return () => {
      alive = false;
      ctrl.abort();
      window.removeEventListener("pagehide", leave);
      leave();
    };
  }, [pairId, signedIn]);

  // When a trick completes, keep its four cards on the table briefly.
  const board = t && t.board;
  useEffect(() => {
    if (!board || !board.lastTrick) return undefined;
    const key = `${board.boardNo}:${board.tricksPlayed}`;
    if (seenTrick.current === key) return undefined;
    const first = seenTrick.current === null;
    seenTrick.current = key;
    if (first || board.trick.length) return undefined; // page just opened, or the next trick has begun
    setLastShown(board.lastTrick);
    clearTimeout(trickTimer.current);
    trickTimer.current = setTimeout(() => setLastShown(null), LAST_TRICK_MS);
    return undefined;
  }, [board]);
  // A new trick under way (or the board over) ends the pause at once.
  useEffect(() => {
    if (lastShown && board && (board.trick.length || board.phase !== "play")) setLastShown(null);
  }, [board, lastShown]);
  useEffect(() => () => clearTimeout(trickTimer.current), []);

  const act = useCallback(async (action, args) => {
    setSending(true);
    try {
      return await ph(action, { pairId, ...args });
    } catch (e) {
      setToast(e.message);
      setTimeout(() => setToast(""), 4000);
      return null;
    } finally {
      setSending(false);
    }
  }, [pairId]);

  if (!authReady && !testUid()) return <div className="pth-root"><div className="pth-page"><p className="pth-muted">Loading…</p></div></div>;
  if (!signedIn) {
    return (
      <div className="pth-root"><div className="pth-page pth-narrow"><div className="pth-card pth-center">
        <p>Sign in to play the weekly tournament with your partner.</p>
        <Link className="pth-btn pth-btn--primary" to="/login">Sign in</Link>
      </div></div></div>
    );
  }
  if (err) {
    return (
      <div className="pth-root"><div className="pth-page pth-narrow"><div className="pth-card pth-center">
        <p className="pth-err" role="alert">{err}</p>
        <Link className="pth-btn pth-btn--ghost" to="/partner">Back to partners</Link>
      </div></div></div>
    );
  }
  if (!t) return <div className="pth-root"><div className="pth-page"><div className="pth-skeleton pth-skeleton--table" aria-label="Loading" /></div></div>;

  const me = t.seat;
  const partnerSeat = me === "S" ? "N" : "S";
  const partnerName = t.names[partnerSeat];
  const pos = positions(me);
  const playing = t.status === "playing" && board && board.phase !== "done";
  const doneCount = Object.keys(t.done).length;
  const closes = new Date(t.closesAt).toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" });

  const play = (card) => act("play", { card });
  const call = (c) => act("call", { call: c });

  const topBar = (
    <div className="pth-tbar">
      <Link to="/partner" className="pth-backLink" aria-label="Back to partners">←</Link>
      <div className="pth-tbarTitle">
        <b>Weekly tournament</b>
        <span className="pth-muted pth-small">{playing ? `Board ${board.boardNo} of ${t.boardCount}` : `${doneCount} of ${t.boardCount} boards · closes ${closes}`}</span>
      </div>
      <span className="pth-grow" />
      <span className={`pth-conn ${t.partnerOnline ? "is-on" : ""}`}><i aria-hidden="true" />{t.partnerOnline ? `${partnerName} is at the table` : t.partnerOnSite ? `${partnerName} is on the site, not at the table` : `${partnerName} is away`}</span>
      <button type="button" className="pth-btn pth-btn--ghost pth-btn--sm" onClick={() => setShowLB(true)}>Leaderboard</button>
    </div>
  );

  const strip = (
    <div className="pth-strip" aria-label="This week's boards">
      {Array.from({ length: t.boardCount }, (_, i) => i + 1).map((n) => {
        const d = t.done[n];
        const cur = playing && board.boardNo === n;
        return (
          <span key={n} className={`pth-bd ${d ? "is-done" : ""} ${cur || (!playing && t.nextBoard === n) ? "is-next" : ""}`} title={d ? `${d.passout ? "Passed out" : ""} ${signed(d.scoreNS)}` : undefined}>
            {n}
            {d && <small>{signed(d.scoreNS)}</small>}
          </span>
        );
      })}
    </div>
  );

  // ── between boards (and before the first) ──
  if (!playing) {
    const finished = t.status === "finished";
    const meReady = !!t.ready[me];
    const partnerReady = !!t.ready[partnerSeat];
    return (
      <div className="pth-root">
        <Helmet><title>Weekly tournament — Bridge Champions</title><meta name="robots" content="noindex" /></Helmet>
        <div className="pth-page pth-page--table">
          {topBar}
          {strip}
          {board && board.phase === "done" && <ResultCard board={board} me={me} />}
          {finished ? (
            <section className="pth-card pth-center">
              <h2 className="pth-h2">All {t.boardCount} boards played</h2>
              <div className="pth-actions pth-actions--center">
                <button type="button" className="pth-btn pth-btn--primary" onClick={() => setShowLB(true)}>Leaderboard</button>
                <Link className="pth-btn pth-btn--ghost" to="/partner">Back to partners</Link>
              </div>
            </section>
          ) : (
            <section className="pth-lobby" aria-label="Waiting room">
              <div className="pth-seats">
                {["S", "N"].map((s) => (
                  <div key={s} className={`pth-seatCard ${t.ready[s] ? "is-ready" : ""} ${s === me ? "is-me" : ""}`}>
                    <Avatar name={t.names[s]} online={t.here[s]} />
                    <h3 className="pth-h3">{t.names[s]}{s === me ? " (you)" : ""}</h3>
                    <div className="pth-muted pth-small">{SEAT_NAME[s]}</div>
                    <div className={`pth-ready ${t.ready[s] ? "is-on" : ""}`}>
                      {t.ready[s] ? "✓ Ready" : t.here[s] ? "Here" : <>Not here yet<span className="pth-ellipsis" /></>}
                    </div>
                  </div>
                ))}
              </div>
              {!t.here[partnerSeat] && (
                <div className="pth-inviteBar">
                  {invitedAt && Date.now() - invitedAt < 5 * 60 * 1000 ? (
                    <span className="pth-muted">Weekly tournament invite sent to {partnerName}<span className="pth-ellipsis" /></span>
                  ) : (
                    <span className="pth-muted">{partnerName} isn't at the weekly tournament table.</span>
                  )}
                  <button
                    type="button"
                    className="pth-btn pth-btn--ghost pth-btn--sm"
                    disabled={sending}
                    onClick={async () => {
                      const r = await act("invitePlay");
                      if (r) { setInvitedAt(Date.now()); window.dispatchEvent(new Event("pth-inbox-refresh")); }
                    }}
                  >
                    {invitedAt ? `Invite ${partnerName} to the weekly tournament again` : `Invite ${partnerName} to the weekly tournament`}
                  </button>
                </div>
              )}
              <div className="pth-center">
                <button
                  type="button"
                  className={`pth-btn ${meReady ? "pth-btn--ghost" : "pth-btn--primary"} pth-btn--big`}
                  disabled={sending}
                  onClick={() => act("ready", { ready: !meReady })}
                >
                  {meReady ? "Not ready" : `Ready for board ${t.nextBoard}`}
                </button>
                <p className="pth-muted pth-small pth-mt">
                  {meReady && !partnerReady
                    ? (t.partnerOnline ? `Waiting for ${partnerName} to be ready` : `Waiting for ${partnerName} to come to the weekly tournament table`)
                    : !meReady && partnerReady ? `${partnerName} is ready` : "The board starts when you are both ready. East-West are played by the bot."}
                </p>
              </div>
            </section>
          )}
          {net && <p className="pth-net">{net}</p>}
        </div>
        {showLB && <Standings pairId={pairId} onClose={() => setShowLB(false)} />}
      </div>
    );
  }

  // ── playing a board ──
  const myMove = board.myMove && !sending ? board.myMove : null;
  const myMoveIsAuction = myMove && board.phase === "auction";
  const showTrick = lastShown ? lastShown.plays : board.trick;
  const winner = lastShown ? lastShown.winner : null;
  const handAt = (seat) => board.hands[seat];
  const canPlayFrom = (seat) => !!myMove && board.phase === "play" && myMove === seat && !lastShown;
  const sideHand = (side) => {
    const seat = pos[side];
    const cards = handAt(seat);
    return (
      <div className={`pth-side pth-side--${side}`}>
        <Plate seat={seat} t={t} board={board} me={me} />
        {cards && <SuitRows cards={cards} legal={board.legal} canPlay={canPlayFrom(seat)} onPlay={play} />}
      </div>
    );
  };
  let banner;
  if (board.phase === "auction") {
    banner = myMoveIsAuction ? "Your call" : board.toAct === partnerSeat ? `${partnerName} to call` : `${SEAT_NAME[board.toAct]} to call`;
  } else {
    const ctrl = board.toAct === board.dummy && (board.declarer === "N" || board.declarer === "S") ? board.declarer : board.toAct;
    banner = myMove ? (myMove === me ? "Your card" : `Play from dummy (${SEAT_NAME[myMove]})`) : ctrl === partnerSeat ? `${partnerName} to play` : `${SEAT_NAME[board.toAct]} to play`;
  }

  return (
    <div className="pth-root">
      <Helmet><title>Weekly tournament — Bridge Champions</title><meta name="robots" content="noindex" /></Helmet>
      <div className="pth-page pth-page--table">
        {topBar}
        <div className="pth-felt">
          <div className="pth-felt-top">
            <Plate seat={pos.top} t={t} board={board} me={me} />
            {handAt(pos.top)
              ? <Fan cards={handAt(pos.top)} legal={board.legal} canPlay={canPlayFrom(pos.top)} onPlay={play} small />
              : <Backs n={board.counts[pos.top]} />}
          </div>
          {sideHand("left")}
          <div className="pth-felt-mid">
            {board.phase === "auction" ? (
              <Auction board={board} />
            ) : (
              <>
                <div className="pth-contractBar">
                  <ContractText contract={board.contract} declarer={board.declarer} />
                  <span className="pth-tricks">N-S {board.ns} · E-W {board.ew}</span>
                </div>
                <Trick plays={showTrick} pos={pos} winner={winner} />
              </>
            )}
          </div>
          {sideHand("right")}
          <div className="pth-felt-bottom">
            <Plate seat={me} t={t} board={board} me={me} />
          </div>
          {!t.partnerOnline && (
            <div className="pth-pause" role="status">
              <div className="pth-pauseCard">
                <Avatar name={partnerName} online={false} />
                <b>Waiting for {partnerName}</b>
                <span className="pth-muted pth-small">The board carries on from here when they're back.</span>
              </div>
            </div>
          )}
        </div>

        <div className={`pth-turn ${myMove ? "is-me" : ""}`} role="status">{banner}</div>

        {myMoveIsAuction && (
          <div className="pth-bidWrap">
            <BiddingBox auction={board.auction} seat={me} onCall={call} disabled={sending} />
          </div>
        )}
        {handAt(me) && (
          <div className="pth-myHand">
            <Fan cards={handAt(me)} legal={board.legal} canPlay={canPlayFrom(me)} onPlay={play} />
          </div>
        )}
        {board.canClaim && !lastShown && (
          <div className="pth-center"><button type="button" className="pth-btn pth-btn--quiet pth-btn--sm" onClick={() => setClaim({ busy: false, msg: "" })}>Claim</button></div>
        )}
        {net && <p className="pth-net">{net}</p>}
      </div>
      {toast && <div className="pth-toast" role="alert">{toast}</div>}
      {showLB && <Standings pairId={pairId} onClose={() => setShowLB(false)} />}
      {claim && (
        <ClaimBox
          left={13 - board.ns - board.ew}
          busy={claim.busy}
          msg={claim.msg}
          onCancel={() => setClaim(null)}
          onClaim={async (n) => {
            setClaim({ busy: true, msg: "" });
            const r = await act("claim", { tricks: n });
            if (r && r.accepted) setClaim(null);
            else setClaim({ busy: false, msg: r ? "The claim doesn't hold. Play on, or claim fewer." : "" });
          }}
        />
      )}
    </div>
  );
}
